// lib/services/CandidateProfileService.ts

import { userProfileApi } from '@/lib/api/userProfileApi';
import { documentApi, Document } from '@/lib/api/documentApi';
import {
    UniversityOption,
    LocationOption,
    WorkScopeOption,
    CandidateProfile,
    CandidateProfileUpdatePayload
} from '@/lib/models/candidateProfile';
export class CandidateProfileService {

    // Mock data for universities in Sweden
    private static mockUniversities: UniversityOption[] = [
        { id: 'gu', name: 'Göteborgs universitet', city: 'Göteborg' },
        { id: 'ki', name: 'Karolinska institutet', city: 'Stockholm' },
        { id: 'kth', name: 'Kungliga Tekniska högskolan', city: 'Stockholm' },
        { id: 'lu', name: 'Lunds universitet', city: 'Lund' },
        { id: 'su', name: 'Stockholms universitet', city: 'Stockholm' },
        { id: 'uu', name: 'Uppsala universitet', city: 'Uppsala' },
        { id: 'chalmers', name: 'Chalmers tekniska högskola', city: 'Göteborg' },
        { id: 'liu', name: 'Linköpings universitet', city: 'Linköping' },
        { id: 'umu', name: 'Umeå universitet', city: 'Umeå' },
        { id: 'mau', name: 'Malmö universitet', city: 'Malmö' },
        { id: 'hig', name: 'Högskolan i Gävle', city: 'Gävle' },
        { id: 'his', name: 'Högskolan i Skövde', city: 'Skövde' },
        { id: 'mdh', name: 'Mälardalens högskola', city: 'Västerås' },
        { id: 'oru', name: 'Örebro universitet', city: 'Örebro' },
        { id: 'vxu', name: 'Linnéuniversitetet', city: 'Växjö' }
    ];

    // Mock data for Swedish cities/locations
    private static mockLocations: LocationOption[] = [
        { id: 'stockholm', name: 'Stockholm', region: 'Stockholm' },
        { id: 'goteborg', name: 'Göteborg', region: 'Västra Götaland' },
        { id: 'malmo', name: 'Malmö', region: 'Skåne' },
        { id: 'uppsala', name: 'Uppsala', region: 'Uppsala' },
        { id: 'linkoping', name: 'Linköping', region: 'Östergötland' },
        { id: 'vasteras', name: 'Västerås', region: 'Västmanland' },
        { id: 'orebro', name: 'Örebro', region: 'Örebro' },
        { id: 'norrkoping', name: 'Norrköping', region: 'Östergötland' },
        { id: 'helsingborg', name: 'Helsingborg', region: 'Skåne' },
        { id: 'jonkoping', name: 'Jönköping', region: 'Jönköping' },
        { id: 'umea', name: 'Umeå', region: 'Västerbotten' },
        { id: 'lund', name: 'Lund', region: 'Skåne' },
        { id: 'boras', name: 'Borås', region: 'Västra Götaland' },
        { id: 'gavle', name: 'Gävle', region: 'Gävleborg' },
        { id: 'eskilstuna', name: 'Eskilstuna', region: 'Södermanland' }
    ];

    // Work scope percentage options
    private static workScopeOptions: WorkScopeOption[] = Array.from({ length: 10 }, (_, i) => ({
        value: (i + 1) * 10,
        label: `${(i + 1) * 10}%`
    }));

    /**
     * Get current user's profile
     */
    static async getUserProfile(userId: string): Promise<CandidateProfile | null> {
        try {
            return await userProfileApi.getCandidateProfile();
        } catch (error) {
            console.error('Error fetching user profile:', error);
            return null;
        }
    }

    /**
     * Update user profile
     */
    static async updateUserProfile(userId: string, payload: CandidateProfileUpdatePayload): Promise<CandidateProfile> {
        try {
            // Try to update existing profile first
            const existingProfile = await userProfileApi.getCandidateProfile();

            if (existingProfile) {
                return await userProfileApi.updateCandidateProfile(payload);
            } else {
                // Create new profile if none exists
                return await userProfileApi.createCandidateProfile(payload);
            }
        } catch (error) {
            console.error('Error updating user profile:', error);
            throw error;
        }
    }

    /**
     * Create user profile
     */
    static async createUserProfile(payload: CandidateProfileUpdatePayload): Promise<CandidateProfile> {
        try {
            return await userProfileApi.createCandidateProfile(payload);
        } catch (error) {
            console.error('Error creating user profile:', error);
            throw error;
        }
    }

    /**
     * Delete user profile
     */
    static async deleteUserProfile(userId: string): Promise<void> {
        try {
            await userProfileApi.deleteCandidateProfile();
        } catch (error) {
            console.error('Error deleting user profile:', error);
            throw error;
        }
    }

    /**
     * Get documents for a candidate profile
     */
    static async getProfileDocuments(profileId: string): Promise<Document[]> {
        try {
            return await documentApi.getDocumentsByEntity('candidate_profile', profileId);
        } catch (error) {
            console.error('Error fetching profile documents:', error);
            return [];
        }
    }

    /**
     * Upload document for candidate profile
     */
    static async uploadDocument(profileId: string, documentType: 'cv' | 'exam_certificate' | 'grade_certificate' | 'other', file: File): Promise<Document> {
        try {
            return await documentApi.uploadFileDocument('candidate_profile', profileId, documentType, file);
        } catch (error) {
            console.error('Error uploading document:', error);
            throw error;
        }
    }

    /**
     * Create text document for candidate profile
     */
    static async createTextDocument(profileId: string, documentType: string, content: string): Promise<Document> {
        try {
            return await documentApi.createTextDocument({
                entity_type: 'candidate_profile',
                entity_id: profileId,
                document_type: documentType,
                content: content
            });
        } catch (error) {
            console.error('Error creating text document:', error);
            throw error;
        }
    }

    /**
     * Delete document
     */
    static async deleteDocument(documentId: string): Promise<void> {
        try {
            await documentApi.deleteDocument(documentId);
        } catch (error) {
            console.error('Error deleting document:', error);
            throw error;
        }
    }

    /**
     * Get document download URL
     */
    static getDocumentDownloadUrl(documentId: string): string {
        return documentApi.getDocumentDownloadUrl(documentId);
    }

    /**
     * Search universities
     */
    static async searchUniversities(query: string): Promise<UniversityOption[]> {
        // Mock delay to simulate API call
        await new Promise(resolve => setTimeout(resolve, 200));

        if (!query) return CandidateProfileService.mockUniversities;

        const filtered = CandidateProfileService.mockUniversities.filter(uni =>
            uni.name.toLowerCase().includes(query.toLowerCase()) ||
            uni.city.toLowerCase().includes(query.toLowerCase())
        );

        return filtered;
    }

    /**
     * Search locations
     */
    static async searchLocations(query: string): Promise<LocationOption[]> {
        // Mock delay to simulate API call
        await new Promise(resolve => setTimeout(resolve, 200));

        if (!query) return CandidateProfileService.mockLocations;

        const filtered = CandidateProfileService.mockLocations.filter(loc =>
            loc.name.toLowerCase().includes(query.toLowerCase()) ||
            loc.region.toLowerCase().includes(query.toLowerCase())
        );

        return filtered;
    }

    /**
     * Get work scope percentage options
     */
    static getWorkScopeOptions(): WorkScopeOption[] {
        return CandidateProfileService.workScopeOptions;
    }
}